import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import {
  Box, Typography, TextField, Select, MenuItem, FormControl, InputLabel,
  Button, Paper, Tabs, Tab, Chip, Autocomplete, Snackbar, Alert,
  IconButton, Divider, Rating, Tooltip, CircularProgress, Grid,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon, Save as SaveIcon,
  ContentCopy as CopyIcon,
} from "@mui/icons-material";
import AdminLayout from "../../components/AdminLayout";
import {
  getExerciseLookups, getExerciseForEdit, createExercise, updateExercise,
} from "../../components/funtionsBD/exerciseManagement";
import { fetchTacticalPatterns, schemaLabel } from "../../components/funtionsBD/tacticalPatternService";

const LANG_CODES = ["en", "fr", "de", "es", "nl", "pt"];

const STROKE_KIND_OPTIONS = [
  "serve",
  "forehand",
  "forehand_topspin",
  "forehand_block",
  "backhand",
  "backhand_topspin",
  "backhand_block",
];
const DEPTHS = ["short", "half_long", "long"];
const WIDTHS = ["bh", "mid", "fh"];
const ZONE_CELLS = DEPTHS.flatMap((d) => WIDTHS.map((w) => `${d}_${w}`));

function parseRecipeJson(raw) {
  if (!raw) return null;
  if (typeof raw === "object") return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function toggleList(list, value) {
  const arr = Array.isArray(list) ? [...list] : [];
  const i = arr.indexOf(value);
  if (i >= 0) arr.splice(i, 1);
  else arr.push(value);
  return arr;
}

function ChipRow({ label, options, selected, onToggle }) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
        {options.map((opt) => (
          <Chip
            key={opt}
            size="small"
            label={opt.replace(/_/g, " ")}
            color={selected.includes(opt) ? "primary" : "default"}
            variant={selected.includes(opt) ? "filled" : "outlined"}
            onClick={() => onToggle(opt)}
          />
        ))}
      </Box>
    </Box>
  );
}

function ZoneGrid({ label, selected, onToggle }) {
  const cells = Array.isArray(selected) ? selected : [];
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
        {label}
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(64px, 1fr))",
          gap: 0.5,
          maxWidth: 280,
        }}
      >
        {ZONE_CELLS.map((cell) => (
          <Chip
            key={cell}
            size="small"
            label={cell.replace(/_/g, " ")}
            color={cells.includes(cell) ? "primary" : "default"}
            variant={cells.includes(cell) ? "filled" : "outlined"}
            onClick={() => onToggle(cell)}
            sx={{ justifyContent: "center" }}
          />
        ))}
      </Box>
    </Box>
  );
}

function SchemaRecipeEditor({ recipeJson, onChange, contextNotes, description, onDescriptionChange, t }) {
  const recipe = parseRecipeJson(recipeJson) || {
    start: "serve",
    mode: "sequence",
    sparring: { kinds: [] },
    slots: [
      { n: 1, kinds: ["serve"], receive: [], send: [], sparring: { kinds: [] } },
      { n: 1, kinds: ["forehand_topspin"], receive: [], send: [], sparring: { kinds: [] } },
    ],
    terminal: null,
  };
  const slots = Array.isArray(recipe.slots) ? recipe.slots : [];
  const serve = slots[0] || { n: 1, kinds: ["serve"], receive: [], send: [], sparring: { kinds: [] } };
  const third = slots[1] || { n: 1, kinds: [], receive: [], send: [], sparring: { kinds: [] } };
  const terminalKinds = recipe.terminal
    ? [recipe.terminal]
    : third.kinds || [];
  const recipeDescription =
    description != null && String(description).trim() !== ""
      ? description
      : recipe.description_en || recipe.description_fr || "";

  const commit = (nextSlots, terminal, descPatch = null) => {
    const next = {
      ...recipe,
      start: "serve",
      mode: "sequence",
      slots: nextSlots,
      terminal: terminal || (nextSlots[1]?.kinds || [])[0] || null,
    };
    if (descPatch && typeof descPatch === "object") Object.assign(next, descPatch);
    onChange(JSON.stringify(next, null, 2));
  };

  const patchSlot = (index, patch) => {
    const next = [...slots];
    while (next.length <= index) {
      next.push({ n: 1, kinds: [], receive: [], send: [], sparring: { kinds: [] } });
    }
    next[index] = { ...next[index], n: 1, ...patch };
    commit(next, recipe.terminal);
  };

  return (
    <Box>
      <TextField
        fullWidth
        multiline
        minRows={2}
        size="small"
        sx={{ mb: 2 }}
        label={t("recipeDescription", "Recipe description")}
        helperText={t(
          "recipeDescriptionHelp",
          "How this schéma is played (serve → 3rd ball → finish). Shown to coaches and players."
        )}
        value={recipeDescription}
        onChange={(e) => {
          const value = e.target.value;
          if (typeof onDescriptionChange === "function") onDescriptionChange(value);
          commit(slots, recipe.terminal, { description_en: value });
        }}
      />
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {t("schemaServe", "Serve")}
      </Typography>
      <ChipRow
        label={t("schemaKinds", "Kinds")}
        options={STROKE_KIND_OPTIONS}
        selected={serve.kinds || []}
        onToggle={(v) => patchSlot(0, { kinds: toggleList(serve.kinds, v) })}
      />
      <ZoneGrid
        label={t("schemaServeSend", "Serve send zones")}
        selected={serve.send || []}
        onToggle={(v) => patchSlot(0, { send: toggleList(serve.send, v) })}
      />
      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
        {t("schemaThird", "3rd ball")}
      </Typography>
      <ChipRow
        label={t("schemaKinds", "Kinds")}
        options={STROKE_KIND_OPTIONS.filter((k) => k !== "serve")}
        selected={third.kinds || []}
        onToggle={(v) => patchSlot(1, { kinds: toggleList(third.kinds, v) })}
      />
      <ZoneGrid
        label={t("schemaThirdReceive", "3rd receive zones")}
        selected={third.receive || []}
        onToggle={(v) => patchSlot(1, { receive: toggleList(third.receive, v) })}
      />
      <ZoneGrid
        label={t("schemaThirdSend", "3rd send zones")}
        selected={third.send || []}
        onToggle={(v) => patchSlot(1, { send: toggleList(third.send, v) })}
      />
      <ChipRow
        label={t("schemaTerminal", "Terminal / finish")}
        options={STROKE_KIND_OPTIONS.filter((k) => k !== "serve")}
        selected={terminalKinds}
        onToggle={(v) => {
          const next = toggleList(terminalKinds, v);
          commit(slots, next[0] || null);
        }}
      />
      {contextNotes?.length ? (
        <Box sx={{ mt: 2, p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {t("schemaContextNotes", "Context notes")}
          </Typography>
          {contextNotes.map((note) => (
            <Typography key={note} variant="body2" sx={{ mt: 0.5 }}>
              {note}
            </Typography>
          ))}
        </Box>
      ) : null}
    </Box>
  );
}

const CONTENT_FIELDS = ["ex_name", "ex_objective", "ex_instruction", "ex_process", "ex_storytelling"];

const getLangField = (obj, prefix) => {
  const lang = localStorage.getItem("i18nextLng")?.substring(0, 2) || "en";
  return obj?.[`${prefix}_${lang}`] || obj?.[`${prefix}_en`] || "";
};

const buildInitialValues = () => {
  const vals = {
    type_id: "", sub_type_id: "", time_id: "", ex_cardio_lev: 0, video_id_example: "",
    themes: [], skills: [], competency_blocks: [], focus_areas: [],
    stroke_recipe: "",
    tactical_pattern_id: "",
  };
  LANG_CODES.forEach((code) => {
    CONTENT_FIELDS.forEach((f) => {
      vals[`${f}_${code}`] = "";
    });
  });
  return vals;
};

export function ExerciseForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { ex_id } = useParams();
  const isEdit = Boolean(ex_id);

  const [lookups, setLookups] = useState(null);
  const [patterns, setPatterns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mainTab, setMainTab] = useState(0);
  const [langTab, setLangTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [showRecipeJson, setShowRecipeJson] = useState(false);

  const validate = (values) => {
    const errors = {};
    if (!values.type_id) errors.type_id = t("fieldRequired");
    if (!values.ex_name_en || !String(values.ex_name_en).trim()) {
      errors.ex_name_en = t("fieldRequired");
    } else if (String(values.ex_name_en).length > 255) {
      errors.ex_name_en = t("fieldRequired");
    }
    return errors;
  };

  const formik = useFormik({
    initialValues: buildInitialValues(),
    validate,
    onSubmit: async (values) => {
      setSaving(true);
      try {
        const payload = {
          basic: {
            type_id: values.type_id,
            sub_type_id: values.sub_type_id || null,
            time_id: values.time_id || null,
            ex_cardio_lev: values.ex_cardio_lev || null,
            video_id_example: values.video_id_example || null,
            stroke_recipe: values.stroke_recipe || null,
            tactical_pattern_id: values.tactical_pattern_id || null,
          },
          content: {},
          relations: {
            themes: values.themes,
            skills: values.skills,
            competency_blocks: values.competency_blocks,
            focus_areas: values.focus_areas,
          },
        };
        LANG_CODES.forEach((code) => {
          CONTENT_FIELDS.forEach((f) => {
            payload.content[`${f}_${code}`] = values[`${f}_${code}`] || null;
          });
        });

        if (isEdit) {
          await updateExercise(ex_id, payload);
          setSnackbar({ open: true, message: t("exerciseUpdated"), severity: "success" });
        } else {
          const res = await createExercise(payload);
          setSnackbar({ open: true, message: t("exerciseCreated"), severity: "success" });
          setTimeout(() => navigate(`/admin/exercise/edit/${res.data.data.ex_id}`), 1200);
        }
      } catch (err) {
        setSnackbar({ open: true, message: err.message || t("errorSaving"), severity: "error" });
      } finally {
        setSaving(false);
      }
    },
  });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const lookupsRes = await getExerciseLookups();
        setLookups(lookupsRes.data.data);
        try {
          const list = await fetchTacticalPatterns();
          setPatterns(Array.isArray(list) ? list : []);
        } catch (_) {
          setPatterns([]);
        }

        if (isEdit) {
          const exRes = await getExerciseForEdit(ex_id);
          const ex = exRes.data.data;
          const vals = buildInitialValues();

          vals.type_id = ex.type_id || "";
          vals.sub_type_id = ex.sub_type_id || "";
          vals.time_id = ex.time_id || "";
          vals.ex_cardio_lev = ex.ex_cardio_lev || 0;
          vals.video_id_example = ex.video_id_example || "";
          vals.stroke_recipe =
            typeof ex.stroke_recipe === "string"
              ? ex.stroke_recipe
              : ex.stroke_recipe
                ? JSON.stringify(ex.stroke_recipe, null, 2)
                : "";
          vals.tactical_pattern_id = ex.tactical_pattern_id || "";

          LANG_CODES.forEach((code) => {
            CONTENT_FIELDS.forEach((f) => {
              vals[`${f}_${code}`] = ex[`${f}_${code}`] || "";
            });
          });

          vals.themes = (ex.tbl_the_exes || []).map((th) => ({
            the_id: th.the_id,
            the_lavel: th.the_lavel || 1,
          }));
          vals.skills = (ex.tbl_skills_exes || []).map((s) => s.sk_id);
          vals.competency_blocks = (ex.tbl_exe_cbs || []).map((cb) => cb.cb_id);
          vals.focus_areas = (ex.tbl_focus_exers || []).map((f) => f.focus_id);

          formik.setValues(vals);
        }
      } catch (err) {
        console.error("Failed to load data:", err);
        setSnackbar({ open: true, message: t("failed_to_load_data"), severity: "error" });
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ex_id, isEdit]);

  const copyFromEnglish = () => {
    const currentLang = LANG_CODES[langTab];
    if (currentLang === "en") return;
    CONTENT_FIELDS.forEach((f) => {
      const enVal = formik.values[`${f}_en`];
      if (enVal && !formik.values[`${f}_${currentLang}`]) {
        formik.setFieldValue(`${f}_${currentLang}`, enVal);
      }
    });
  };

  const filteredSubTypes = useMemo(() => {
    if (!lookups?.subTypes) return [];
    return lookups.subTypes.filter(
      (st) => !formik.values.type_id || st.type_id === formik.values.type_id || !st.type_id
    );
  }, [lookups, formik.values.type_id]);

  if (loading) {
    return (
      <AdminLayout>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box sx={{ p: 2 }}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2 }}>
          <IconButton onClick={() => navigate("/ExerciseStudio")}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h5" fontWeight="bold">
              {isEdit ? t("editExercise") : t("addExerciseCatalog")}
            </Typography>
            {isEdit && (
              <Typography variant="body1" color="text.secondary" sx={{ mt: 0.25 }}>
                {getLangField(formik.values, "ex_name") || formik.values.ex_name_en || ""}
              </Typography>
            )}
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={formik.handleSubmit}
            disabled={saving}
            sx={{ bgcolor: "var(--brand-primary)", "&:hover": { bgcolor: "var(--brand-primary-dark)" } }}
          >
            {saving ? t("saving") : t("save")}
          </Button>
        </Box>

        {/* Main tabs */}
        <Paper sx={{ mb: 2 }}>
          <Tabs value={mainTab} onChange={(_, v) => setMainTab(v)} variant="fullWidth">
            <Tab label={t("basicInfo")} />
            <Tab label={t("content")} />
            <Tab label={t("relations")} />
            <Tab label={t("strokeRecipe", "Stroke recipe")} />
          </Tabs>
        </Paper>

        {/* Tab 0: Basic Info */}
        {mainTab === 0 && (
          <Paper sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={Boolean(formik.touched.type_id && formik.errors.type_id)}>
                  <InputLabel>{t("exerciseType")} *</InputLabel>
                  <Select
                    name="type_id"
                    value={formik.values.type_id}
                    label={`${t("exerciseType")} *`}
                    onChange={(e) => {
                      formik.setFieldValue("type_id", e.target.value);
                      formik.setFieldValue("sub_type_id", "");
                    }}
                    onBlur={formik.handleBlur}
                  >
                    {(lookups?.types || []).map((tp) => (
                      <MenuItem key={tp.type_id} value={tp.type_id}>
                        {getLangField(tp, "type_name")}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.type_id && formik.errors.type_id && (
                    <Typography variant="caption" color="error">{formik.errors.type_id}</Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{t("exerciseSubType")}</InputLabel>
                  <Select
                    name="sub_type_id"
                    value={formik.values.sub_type_id}
                    label={t("exerciseSubType")}
                    onChange={formik.handleChange}
                  >
                    <MenuItem value="">{t("notselect")}</MenuItem>
                    {filteredSubTypes.map((st) => (
                      <MenuItem key={st.sub_type_id} value={st.sub_type_id}>
                        {getLangField(st, "sub_type_name")}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>{t("duration")}</InputLabel>
                  <Select
                    name="time_id"
                    value={formik.values.time_id}
                    label={t("duration")}
                    onChange={formik.handleChange}
                  >
                    <MenuItem value="">{t("notselect")}</MenuItem>
                    {(lookups?.times || []).map((tm) => (
                      <MenuItem key={tm.time_id} value={tm.time_id}>
                        {tm.time_value} {tm.time_type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t("cardioLevel")}
                  </Typography>
                  <Rating
                    name="ex_cardio_lev"
                    value={formik.values.ex_cardio_lev}
                    max={5}
                    onChange={(_, newValue) => formik.setFieldValue("ex_cardio_lev", newValue)}
                    sx={{ color: "#f44336" }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  name="video_id_example"
                  label={t("videoReference")}
                  value={formik.values.video_id_example}
                  onChange={formik.handleChange}
                  placeholder="Video ID or URL"
                />
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Tab 1: Content */}
        {mainTab === 1 && (
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1 }}>
              <Tabs value={langTab} onChange={(_, v) => setLangTab(v)}>
                {LANG_CODES.map((code, i) => (
                  <Tab
                    key={code}
                    label={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        {code === "en" && t("language_english")}
                        {code === "fr" && t("language_french")}
                        {code === "de" && t("language_german")}
                        {code === "es" && t("language_spanish")}
                        {code === "nl" && t("language_dutch")}
                    {code === "pt" && t("language_portuguese")}
                        {formik.values[`ex_name_${code}`] && (
                          <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#4caf50" }} />
                        )}
                      </Box>
                    }
                  />
                ))}
              </Tabs>
              <Box sx={{ flexGrow: 1 }} />
              {LANG_CODES[langTab] !== "en" && (
                <Tooltip title={t("copyFromEN")}>
                  <Button size="small" startIcon={<CopyIcon />} onClick={copyFromEnglish} variant="outlined">
                    {t("copyFromEN")}
                  </Button>
                </Tooltip>
              )}
            </Box>
            <Divider sx={{ mb: 2 }} />

            {(() => {
              const lang = LANG_CODES[langTab];
              const isRequired = lang === "en";
              return (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                  <TextField
                    fullWidth
                    name={`ex_name_${lang}`}
                    label={`${t("exercise_name")} ${isRequired ? "*" : ""}`}
                    value={formik.values[`ex_name_${lang}`]}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={isRequired && formik.touched[`ex_name_${lang}`] && Boolean(formik.errors[`ex_name_${lang}`])}
                    helperText={isRequired && formik.touched[`ex_name_${lang}`] && formik.errors[`ex_name_${lang}`]}
                  />
                  <TextField
                    fullWidth multiline rows={3}
                    name={`ex_objective_${lang}`}
                    label={t("objective")}
                    value={formik.values[`ex_objective_${lang}`]}
                    onChange={formik.handleChange}
                  />
                  <TextField
                    fullWidth multiline rows={3}
                    name={`ex_instruction_${lang}`}
                    label={t("instruction")}
                    value={formik.values[`ex_instruction_${lang}`]}
                    onChange={formik.handleChange}
                  />
                  <TextField
                    fullWidth multiline rows={3}
                    name={`ex_process_${lang}`}
                    label={t("procedure")}
                    value={formik.values[`ex_process_${lang}`]}
                    onChange={formik.handleChange}
                  />
                  <TextField
                    fullWidth multiline rows={3}
                    name={`ex_storytelling_${lang}`}
                    label={t("storytelling")}
                    value={formik.values[`ex_storytelling_${lang}`]}
                    onChange={formik.handleChange}
                  />
                </Box>
              );
            })()}
          </Paper>
        )}

        {/* Tab 2: Relations */}
        {mainTab === 2 && (
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {/* Themes */}
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>{t("themes")}</Typography>
                <Autocomplete
                  multiple
                  options={lookups?.themes || []}
                  getOptionLabel={(opt) => getLangField(opt, "theme_name")}
                  value={(lookups?.themes || []).filter((th) =>
                    formik.values.themes.some((v) => v.the_id === th.th_id)
                  )}
                  onChange={(_, newVal) => {
                    const current = formik.values.themes;
                    const updated = newVal.map((th) => {
                      const existing = current.find((c) => c.the_id === th.th_id);
                      return { the_id: th.th_id, the_lavel: existing?.the_lavel || 1 };
                    });
                    formik.setFieldValue("themes", updated);
                  }}
                  isOptionEqualToValue={(opt, val) => opt.th_id === val.th_id}
                  renderInput={(params) => <TextField {...params} label={t("themes")} placeholder={t("search")} />}
                  size="small"
                />
                {formik.values.themes.length > 0 && (
                  <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {formik.values.themes.map((th) => {
                      const themeData = lookups?.themes?.find((t) => t.th_id === th.the_id);
                      return (
                        <Chip
                          key={th.the_id}
                          label={
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              {getLangField(themeData, "theme_name")}
                              <Select
                                size="small"
                                variant="standard"
                                value={th.the_lavel}
                                onChange={(e) => {
                                  const updated = formik.values.themes.map((t) =>
                                    t.the_id === th.the_id ? { ...t, the_lavel: e.target.value } : t
                                  );
                                  formik.setFieldValue("themes", updated);
                                }}
                                sx={{ minWidth: 40, fontSize: "0.75rem" }}
                              >
                                {[1, 2, 3, 4, 5].map((lvl) => (
                                  <MenuItem key={lvl} value={lvl} sx={{ fontSize: "0.75rem" }}>L{lvl}</MenuItem>
                                ))}
                              </Select>
                            </Box>
                          }
                          variant="outlined"
                          size="small"
                        />
                      );
                    })}
                  </Box>
                )}
              </Box>

              <Divider />

              {/* Skills */}
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>{t("skills")}</Typography>
                <Autocomplete
                  multiple
                  options={lookups?.skills || []}
                  getOptionLabel={(opt) => getLangField(opt, "skill_name")}
                  value={(lookups?.skills || []).filter((sk) =>
                    formik.values.skills.includes(sk.sk_id)
                  )}
                  onChange={(_, newVal) => {
                    formik.setFieldValue("skills", newVal.map((s) => s.sk_id));
                  }}
                  isOptionEqualToValue={(opt, val) => opt.sk_id === val.sk_id}
                  renderInput={(params) => <TextField {...params} label={t("skills")} placeholder={t("search")} />}
                  size="small"
                />
              </Box>

              <Divider />

              {/* Competency Blocks */}
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>{t("competencyBlocks")}</Typography>
                <Autocomplete
                  multiple
                  options={lookups?.competencyBlocks || []}
                  getOptionLabel={(opt) => getLangField(opt, "cb_name")}
                  value={(lookups?.competencyBlocks || []).filter((cb) =>
                    formik.values.competency_blocks.includes(cb.cb_id)
                  )}
                  onChange={(_, newVal) => {
                    formik.setFieldValue("competency_blocks", newVal.map((cb) => cb.cb_id));
                  }}
                  isOptionEqualToValue={(opt, val) => opt.cb_id === val.cb_id}
                  renderInput={(params) => <TextField {...params} label={t("competencyBlocks")} placeholder={t("search")} />}
                  size="small"
                />
              </Box>

              <Divider />

              {/* Focus Areas */}
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>{t("focus")}</Typography>
                <Autocomplete
                  multiple
                  options={lookups?.focusAreas || []}
                  getOptionLabel={(opt) => getLangField(opt, "focus_description")}
                  value={(lookups?.focusAreas || []).filter((f) =>
                    formik.values.focus_areas.includes(f.focus_id)
                  )}
                  onChange={(_, newVal) => {
                    formik.setFieldValue("focus_areas", newVal.map((f) => f.focus_id));
                  }}
                  isOptionEqualToValue={(opt, val) => opt.focus_id === val.focus_id}
                  renderInput={(params) => <TextField {...params} label={t("focus")} placeholder={t("search")} />}
                  size="small"
                />
              </Box>
            </Box>
          </Paper>
        )}

        {mainTab === 3 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              {t("strokeRecipe", "Stroke recipe")}
            </Typography>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>{t("schemaKey", "Schéma de jeu")}</InputLabel>
              <Select
                label={t("schemaKey", "Schéma de jeu")}
                value={formik.values.tactical_pattern_id || ""}
                onChange={(e) => {
                  const id = e.target.value;
                  formik.setFieldValue("tactical_pattern_id", id);
                  const hit = patterns.find((p) => p.pattern_id === id);
                  if (hit?.stroke_recipe) {
                    formik.setFieldValue(
                      "stroke_recipe",
                      JSON.stringify(
                        {
                          ...(typeof hit.stroke_recipe === "object"
                            ? hit.stroke_recipe
                            : {}),
                          description_en:
                            hit.description_en ||
                            hit.description ||
                            hit.stroke_recipe?.description_en ||
                            "",
                          description_fr:
                            hit.description_fr ||
                            hit.stroke_recipe?.description_fr ||
                            "",
                        },
                        null,
                        2
                      )
                    );
                    setShowRecipeJson(false);
                  }
                }}
              >
                <MenuItem value="">{t("none", "None")}</MenuItem>
                {patterns.map((p) => (
                  <MenuItem key={p.pattern_id} value={p.pattern_id}>
                    {p.name || schemaLabel(p.schema_key)} ({p.schema_key})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {formik.values.tactical_pattern_id ? (
              <SchemaRecipeEditor
                recipeJson={formik.values.stroke_recipe}
                onChange={(json) => formik.setFieldValue("stroke_recipe", json)}
                description={
                  patterns.find((p) => p.pattern_id === formik.values.tactical_pattern_id)
                    ?.description ||
                  patterns.find((p) => p.pattern_id === formik.values.tactical_pattern_id)
                    ?.description_en ||
                  ""
                }
                contextNotes={(
                  patterns.find((p) => p.pattern_id === formik.values.tactical_pattern_id)
                    ?.context_playbooks || []
                )
                  .map((row) => row.coach_note)
                  .filter(Boolean)
                  .slice(0, 4)}
                t={t}
              />
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t(
                  "strokeRecipeHint",
                  "JSON: start (serve|open_ball|either), mode (iterative|sequence), sparring { kinds } for all replies, slots [{ n, kinds, receive, send, sparring }]. Slot sparring overrides the recipe default. Zones: short_bh … long_fh or shorthand short/long/bh/fh."
                )}
              </Typography>
            )}
            <Box sx={{ display: "flex", gap: 1, my: 2 }}>
              {!formik.values.tactical_pattern_id ? (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const type = (lookups?.types || []).find((tp) => tp.type_id === formik.values.type_id);
                    const sub = (lookups?.subTypes || []).find((st) => st.sub_type_id === formik.values.sub_type_id);
                    const blob = JSON.stringify({
                      start: /service|serve/i.test(type?.type_name_en || "") ? "serve" : "either",
                      mode: "iterative",
                      sparring: { kinds: [] },
                      slots: [
                        {
                          n: 1,
                          kinds: ["forehand", "forehand_topspin", "backhand", "backhand_topspin"],
                          receive: [],
                          send: [],
                          sparring: { kinds: [] },
                        },
                      ],
                      terminal: null,
                      _hint_type: type?.type_name_en || "",
                      _hint_subtype: sub?.sub_type_name_en || "",
                    }, null, 2);
                    formik.setFieldValue("stroke_recipe", blob);
                  }}
                >
                  {t("suggestRecipe", "Insert template")}
                </Button>
              ) : null}
              <Button
                size="small"
                variant="text"
                onClick={() => setShowRecipeJson((v) => !v)}
              >
                {showRecipeJson
                  ? t("hideRecipeJson", "Hide JSON")
                  : t("showRecipeJson", "Show JSON")}
              </Button>
            </Box>
            {(showRecipeJson || !formik.values.tactical_pattern_id) ? (
              <TextField
                fullWidth
                multiline
                minRows={formik.values.tactical_pattern_id ? 8 : 14}
                name="stroke_recipe"
                label={t("strokeRecipeJson", "Recipe JSON")}
                value={formik.values.stroke_recipe}
                onChange={formik.handleChange}
                sx={{ fontFamily: "monospace" }}
              />
            ) : null}
          </Paper>
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AdminLayout>
  );
}
